/**
 * Locale registry for the customer app (apps/web).
 * Switchable now: sv, en.
 * Shown as labels only (not switchable yet): da, fi, is, nb.
 * Full catalogs live at repo root: i18n/messages/*.json
 */

export type AppLocale = "sv" | "en";

/** Codes that may appear in the switcher UI */
export type PickerLocale = "sv" | "en" | "da" | "fi" | "is" | "nb";

export type LocaleInfo = {
  code: PickerLocale;
  nativeName: string;
  /** When false, LanguageSwitcher shows label only — no setLocale */
  switchable: boolean;
};

export const DEFAULT_LOCALE: AppLocale = "sv";
export const FALLBACK_LOCALE: AppLocale = "en";

export const STORAGE_KEY = "gdpr-nordic-locale";

/** Locales the app can actually load messages for today */
export const SWITCHABLE_LOCALES: readonly AppLocale[] = ["sv", "en"] as const;

/**
 * Full picker row list.
 * DA / FI / IS / NB: label only until Agent 8 phase-2 (or follow-up) loads their catalogs.
 */
export const PICKER_LOCALES: readonly LocaleInfo[] = [
  { code: "sv", nativeName: "Svenska", switchable: true },
  { code: "en", nativeName: "English", switchable: true },
  { code: "da", nativeName: "Dansk", switchable: false },
  { code: "fi", nativeName: "Suomi", switchable: false },
  { code: "is", nativeName: "Íslenska", switchable: false },
  { code: "nb", nativeName: "Norsk", switchable: false },
] as const;

export function isAppLocale(code: string): code is AppLocale {
  return code === "sv" || code === "en";
}

export function readStoredLocale(): AppLocale | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && isAppLocale(raw)) return raw;
  } catch {
    /* private mode / SSR */
  }
  return null;
}

export function writeStoredLocale(locale: AppLocale): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
}
